import React, { FC, useState } from 'react';

import Done from '@mui/icons-material/Done';
import CircleOutlined from '@mui/icons-material/CircleOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Box,
	Checkbox,
	FormControlLabel,
	FormGroup,
	ListItem,
	Typography,
	ButtonBase,
} from '@mui/material';
import { useFormikContext } from 'formik';

interface IForm {
	optOut: boolean;
}

interface IAutoRenewSubscriptionProps {
	onChange: (optOut: boolean) => void;
}

const AutoRenewSubscription: FC<IAutoRenewSubscriptionProps> = ({ onChange }) => {
	const formik = useFormikContext<IForm>();
	const [openOptOut, toggleOptOut] = useState(false);

	const handleOptOutSelect = (_event: React.ChangeEvent<HTMLInputElement>, value: boolean) => {
		formik.setFieldValue('optOut', value);
		if (onChange) onChange(value);
	};

	const expandIcon: React.JSX.Element = (
		<ExpandMore onClick={() => toggleOptOut(!openOptOut)} sx={{ cursor: 'pointer' }} />
	);

	return (
		<Box>
			<Typography variant='body1' gutterBottom fontWeight='bold'>
				Your renewal of your Subscription Policy
			</Typography>
			<Accordion elevation={0} expanded={openOptOut} sx={{ cursor: 'unset' }}>
				<AccordionSummary
					sx={{ cursor: 'unset !important', padding: 0 }}
					aria-controls='renewal-opt-out-content'
					id='renewal-opt-out-header'
					expandIcon={expandIcon}
				>
					<Typography>
						To make things easier and for your continued protection, your policy is set to automatically renew every 30
						days. To do this, we&apos;ll securely store your card details from today&apos;s payment, and we will collect
						your next payment on the day your 30-day subscription expires. Provided your payment is successful, we will
						issue you with new policy documents on the day of expiry of your existing policy. If your payment is not
						successful, your policy will automatically expire. By selecting this, you are agreeing to a continuous
						payment authority being set up from your bank account which you can cancel at any time free of charge. If
						you would like to opt out of automatic renewal, you can do this now - please click&nbsp;
						<ButtonBase
							onClick={() => toggleOptOut((prev) => !prev)}
							sx={{
								fontSize: '1rem',
								color: 'primary.main',
								verticalAlign: 'baseline',
								padding: 0,
								lineHeight: 'inherit',
							}}
						>
							here.
						</ButtonBase>
					</Typography>
				</AccordionSummary>

				<AccordionDetails sx={{ p: 0 }}>
					<Box>
						<Typography variant='body1' gutterBottom fontWeight='bold'>
							Why auto-renew?
						</Typography>
						<ListItem>
							<Typography>Do you want your policy to automatically roll over?</Typography>
						</ListItem>
						<ListItem>
							<Typography />
						</ListItem>
						<ListItem>
							<Box mr={2}>
								<Done fontSize='large' color='primary' />
							</Box>
							<Typography>
								Setting your policy to auto-renew helps keep you protected so you are not left without cover. It is an
								offence under the Road Traffic Act 1988 to leave, drive or otherwise use your vehicle on a public
								highway without a valid insurance cover being in force. Choosing not to automatically renew means your
								policy will end on your renewal date unless you contact us to continue cover or arrange alternative
								cover with another provider.
							</Typography>
						</ListItem>
						<ListItem>
							<Box mr={2}>
								<Done fontSize='large' color='primary' />
							</Box>
							<Typography>
								We will email you approximately 7 days before your policy is due to expire, giving you plenty of time to
								cancel your auto-renewal subscription should you wish to do so.
							</Typography>
						</ListItem>
						<ListItem>
							<Box mr={2}>
								<Done fontSize='large' color='primary' />
							</Box>
							<Typography>
								If you change your mind at any point, you can opt out of the auto-renewal subscription easily at any
								time via your online account or by contacting us.
							</Typography>
						</ListItem>
						<ListItem>
							<Box mr={2}>
								<Done fontSize='large' color='primary' />
							</Box>
							<Typography>
								You can also cancel your continuous payment authority at any time by either contacting us directly or
								contacting your card issuer.
							</Typography>
						</ListItem>
					</Box>
					<Box marginBottom={4}>
						<FormGroup>
							<FormControlLabel
								sx={{ my: 2 }}
								control={
									<Checkbox
										icon={<CircleOutlined />}
										checkedIcon={<CheckCircle />}
										checked={!formik.values.optOut}
										id='renewal-opt-out-yes'
										onChange={(event) => {
											handleOptOutSelect(event, false);
										}}
										sx={{
											'& .MuiSvgIcon-root': { fontSize: '4rem' },
										}}
									/>
								}
								label='Yes, I’m happy with this. I understand that my automatic renewal is subject to payment being
											 successfully collected from my card payment on the date when my current policy is due to expire.'
							/>
							<FormControlLabel
								control={
									<Checkbox
										icon={<CircleOutlined />}
										checkedIcon={<CheckCircle />}
										checked={formik.values.optOut}
										id='renewal-opt-out-no'
										onChange={(event) => {
											handleOptOutSelect(event, true);
										}}
										sx={{
											'& .MuiSvgIcon-root': { fontSize: '4rem' },
										}}
									/>
								}
								label='No, I would like to opt out of the subscription option now. I understand that by choosing not
											 to automatically renew, my policy will end on the expiry date unless I contact you to continue cover.'
							/>
						</FormGroup>
					</Box>
				</AccordionDetails>
			</Accordion>
		</Box>
	);
};

export default AutoRenewSubscription;
